import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose
} from '../../ui/drawer'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group'
import { Plus, Trash2, GripVertical } from 'lucide-react'
import { requestCreator } from '../../../utilities/requests'
import { SPORT_OPTIONS } from '../../../utilities/sportOptions'

const { get: getCurriculums, cancel: cancelCurriculumsRequest } = requestCreator()

function CurriculumDrawer({
  open,
  onOpenChange,
  facilityId,
  selectedSport,
  selectedCurriculumId,
  selectedSkillLevelIds,
  customSkillLevels,
  curriculumMode,
  onSportChange,
  onCurriculumChange,
  onSkillLevelIdsChange,
  onCustomSkillLevelsChange,
  onModeChange,
  onCurriculumSkillLevelsChange
}) {
  const [curriculums, setCurriculums] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [localCustomLevels, setLocalCustomLevels] = useState(customSkillLevels || [])

  useEffect(() => {
    if (open && facilityId) {
      loadCurriculums()
    }
    return cancelCurriculumsRequest
  }, [open, facilityId])

  useEffect(() => {
    setLocalCustomLevels(customSkillLevels || [])
  }, [customSkillLevels])

  function loadCurriculums() {
    setIsLoading(true)
    getCurriculums(`/api/facilities/${facilityId}/curriculums`, {
      params: { include: 'skill_levels' }
    })
      .then((data) => {
        setCurriculums(data)
        if (selectedCurriculumId && onCurriculumSkillLevelsChange) {
          const curriculum = data.find((c) => String(c.id) === String(selectedCurriculumId))
          if (curriculum) {
            const skillLevels = (curriculum.skillLevels || curriculum.skill_levels || []).map((sl) => ({
              ...sl,
              label: sl.name,
              value: sl.id
            }))
            onCurriculumSkillLevelsChange(skillLevels, curriculum.name, false)
          }
        }
        setIsLoading(false)
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error('Error loading curriculums:', error)
        }
        setIsLoading(false)
      })
  }

  const selectedCurriculum = curriculums.find((c) => String(c.id) === String(selectedCurriculumId))
  const filteredCurriculums = curriculums.filter((c) => c.sport === selectedSport)

  function handleAddCustomLevel() {
    const newLevel = {
      id: `temp-${Date.now()}`,
      name: '',
      skills: []
    }
    const updated = [...localCustomLevels, newLevel]
    setLocalCustomLevels(updated)
    onCustomSkillLevelsChange(updated)
  }

  function handleRemoveCustomLevel(index) {
    const updated = localCustomLevels.filter((_, i) => i !== index)
    setLocalCustomLevels(updated)
    onCustomSkillLevelsChange(updated)
  }

  function handleCustomLevelNameChange(index, name) {
    const updated = localCustomLevels.map((level, i) => (i === index ? { ...level, name } : level))
    setLocalCustomLevels(updated)
    onCustomSkillLevelsChange(updated)
  }

  function handleAddSkill(levelIndex) {
    const updated = localCustomLevels.map((level, i) => {
      if (i === levelIndex) {
        return {
          ...level,
          skills: [...(level.skills || []), { name: '', ordinal: (level.skills?.length || 0) + 1 }]
        }
      }
      return level
    })
    setLocalCustomLevels(updated)
    onCustomSkillLevelsChange(updated)
  }

  function handleSkillNameChange(levelIndex, skillIndex, name) {
    const updated = localCustomLevels.map((level, i) => {
      if (i === levelIndex) {
        const updatedSkills = level.skills.map((skill, si) => (si === skillIndex ? { ...skill, name } : skill))
        return { ...level, skills: updatedSkills }
      }
      return level
    })
    setLocalCustomLevels(updated)
    onCustomSkillLevelsChange(updated)
  }

  function handleRemoveSkill(levelIndex, skillIndex) {
    const updated = localCustomLevels.map((level, i) => {
      if (i === levelIndex) {
        return { ...level, skills: level.skills.filter((_, si) => si !== skillIndex) }
      }
      return level
    })
    setLocalCustomLevels(updated)
    onCustomSkillLevelsChange(updated)
  }

  function handleCurriculumSelect(curriculumId) {
    const curriculum = curriculums.find((c) => String(c.id) === String(curriculumId))
    onCurriculumChange(curriculumId)
    if (onCurriculumSkillLevelsChange && curriculum) {
      const skillLevels = (curriculum.skillLevels || curriculum.skill_levels || []).map((sl) => ({
        ...sl,
        label: sl.name,
        value: sl.id
      }))
      onCurriculumSkillLevelsChange(skillLevels, curriculum.name)
    }
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Sport & Curriculum</DrawerTitle>
          <DrawerDescription>Select the sport type and curriculum for this class.</DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 p-6 space-y-6 overflow-y-auto">
          <div>
            <Label className="mb-2 block">Sport Type</Label>
            <Select value={selectedSport} onValueChange={onSportChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a sport" />
              </SelectTrigger>
              <SelectContent>
                {SPORT_OPTIONS.map((sport) => (
                  <SelectItem key={sport.value} value={sport.value}>
                    {sport.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-3 block">Curriculum</Label>
            <RadioGroup value={curriculumMode} onValueChange={onModeChange} className="space-y-3">
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="existing" id="mode-existing" />
                <div className="flex-1">
                  <Label htmlFor="mode-existing" className="font-medium cursor-pointer">
                    Use existing curriculum
                  </Label>
                  {curriculumMode === 'existing' && (
                    <div className="mt-3 space-y-3">
                      {isLoading ? (
                        <p className="text-sm text-gray-500">Loading curriculums...</p>
                      ) : filteredCurriculums.length === 0 ? (
                        <p className="text-sm text-gray-500">No curriculums available for this sport.</p>
                      ) : (
                        <Select
                          value={selectedCurriculumId?.toString() || ''}
                          onValueChange={(val) => handleCurriculumSelect(parseInt(val))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a curriculum" />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredCurriculums.map((curriculum) => (
                              <SelectItem key={curriculum.id} value={curriculum.id.toString()}>
                                {curriculum.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <RadioGroupItem value="custom" id="mode-custom" />
                <div className="flex-1">
                  <Label htmlFor="mode-custom" className="font-medium cursor-pointer">
                    Create custom skill levels
                  </Label>
                  {curriculumMode === 'custom' && (
                    <div className="mt-3 space-y-4">
                      {localCustomLevels.map((level, levelIndex) => (
                        <div key={level.id} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center gap-2 mb-3">
                            <GripVertical className="w-4 h-4 text-gray-400" />
                            <Input
                              placeholder="Skill Level Name (e.g., Beginner)"
                              value={level.name}
                              onChange={(e) => handleCustomLevelNameChange(levelIndex, e.target.value)}
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveCustomLevel(levelIndex)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>

                          <div className="ml-6 space-y-2">
                            {level.skills?.map((skill, skillIndex) => (
                              <div key={skillIndex} className="flex items-center gap-2">
                                <Input
                                  placeholder="Skill name"
                                  value={skill.name}
                                  onChange={(e) => handleSkillNameChange(levelIndex, skillIndex, e.target.value)}
                                  className="flex-1 text-sm"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveSkill(levelIndex, skillIndex)}
                                >
                                  <Trash2 className="w-3 h-3 text-gray-400" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAddSkill(levelIndex)}
                              className="text-purple-600"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Skill
                            </Button>
                          </div>
                        </div>
                      ))}

                      <Button type="button" variant="outline" onClick={handleAddCustomLevel} className="w-full">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Skill Level
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </RadioGroup>
          </div>
        </div>

        <DrawerFooter className="border-t border-gray-200">
          <DrawerClose asChild>
            <Button variant="outline" className="w-full">
              Done
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

CurriculumDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  facilityId: PropTypes.string.isRequired,
  selectedSport: PropTypes.string,
  selectedCurriculumId: PropTypes.number,
  selectedSkillLevelIds: PropTypes.array,
  customSkillLevels: PropTypes.array,
  curriculumMode: PropTypes.oneOf(['existing', 'custom']),
  onSportChange: PropTypes.func.isRequired,
  onCurriculumChange: PropTypes.func.isRequired,
  onSkillLevelIdsChange: PropTypes.func.isRequired,
  onCustomSkillLevelsChange: PropTypes.func.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onCurriculumSkillLevelsChange: PropTypes.func
}

export default CurriculumDrawer

